import type { ProductItem } from './product';

/** Document representing a link to a product. */
type ProductSimpleLink = {
    /** The target of the link. */
    link?: string;
    /** The link title. */
    title?: string;
};

/** * Result document of product list addresses. */
type CustomerAddressLink = {
    /**
     * The id of the address.
     * @maxLength 256
     */
    address_id?: string;
    /** The target of the link. */
    link?: string;
    /** The link title. */
    title?: string;
};

enum CustomerProductListItemType {
    PRODUCT = 'product',
    GIFT_CERTIFICATe = 'gift_certificate',
}

/** Document representing a customer product list item. */
type CustomerProductListItem = {
    /** The id of this product list item. */
    id?: string;
    /**
     * The priority of the item.
     * @format int32
     */
    priority?: number;
    /** The product item */
    product?: ProductItem;
    /** A link to the product. */
    product_details_link?: ProductSimpleLink;
    /** The id of the product. */
    product_id?: string;
    /** Is this product list item available for access by other customers? */
    public?: boolean;
    /**
     * The quantity of products already purchased.
     * @format double
     */
    purchased_quantity?: number;
    /**
     * The quantity of this product list item.
     * @format double
     * @min 0
     */
    quantity?: number;
    /** The type of the item. */
    type?: CustomerProductListItemType;
};

/** Document representing a customer product list registrant. */
type CustomerProductListRegistrant = {
    /** The email of the registrant. */
    email?: string;
    /** The first name of the registrant. */
    first_name?: string;
    /** The last name of the registrant. */
    last_name?: string;
    /** The role of the registrant. */
    role?: string;
};

/** Document representing a customer product list item link. */
type CustomerProductListItemLink = {
    /** The target of the link. */
    link?: string;
    /** The link title. */
    title?: string;
};

/** Document representing a product list event. */
type ProductListEvent = {
    /** The city where the event takes place. */
    city?: string;
    /** The country where the event takes place. */
    country?: string;
    /**
     * The date when the event takes place.
     * @format date-time
     */
    date?: string;
    /** The federal state where the event takes place. */
    state?: string;
    /** Type of the event to celebrate. */
    type?: string;
};

/** Document representing a product list shipping address. */
type ProductListShippingAddress = {
    /**
     * The id of this address.
     * @minLength 1
     */
    address_id: string;
    /** The city of this address. */
    city?: string;
    /** The first name of this address. */
    first_name?: string;
    /** The last name of this address. */
    last_name?: string;
};

enum CustomerProductListType {
    WISH_LIST = 'wish_list',
    GIFT_REGISTRY = 'gift_registry',
    SHOPPING_LIST = 'shopping_list',
    CUSTOM_1 = 'custom_1',
    CUSTOM_2 = 'custom_2',
    CUSTOM_3 = 'custom_3',
}

/** Document representing a customer product List. */
type CustomerProductList = {
    /** The coRegistrant of this product list. */
    co_registrant?: CustomerProductListRegistrant;
    /**
     * Returns the value of attribute 'creationDate'.
     * @format date-time
     */
    creation_date?: string;
    /** The resource link to the current shipping address of this customer product list. */
    current_shipping_address_link?: CustomerAddressLink;
    /** The list of customer product list items. */
    customer_product_list_items?: CustomerProductListItem[];
    /** The description of this product list. */
    description?: string;
    /** The event of this product list. */
    event?: ProductListEvent;
    /**
     * The id of this product list.
     * @minLength 1
     */
    id?: string;
    /** The resource link to the items of this customer product list. */
    items_link?: CustomerProductListItemLink;
    /**
     * Returns the value of attribute 'lastModified'.
     * @format date-time
     */
    last_modified?: string;
    /** The name of this product list. */
    name?: string;
    /** The resource link to the post event shipping address of this customer product list. */
    post_event_shipping_address_link?: CustomerAddressLink;
    /** The abbreviated shipping address of this product list representing what anonymous user can see. */
    product_list_shipping_address?: ProductListShippingAddress;
    /** Indicates whether the owner made this product list available for access by other customers. */
    public?: boolean;
    /** The registrant of this product list. */
    registrant?: CustomerProductListRegistrant;
    /** The resource link to the shipping address of this customer product list. */
    shipping_address_link?: CustomerAddressLink;
    /** The type of the product list. */
    type?: CustomerProductListType;
};

/** Document representing a customer product lists result. */
type CustomerProductListResult = {
    /**
     * The number of returned documents.
     * @format int32
     */
    count?: number;
    /** The customer product lists. */
    data?: CustomerProductList[];
    next?: string;
    previous?: string;
    /**
     * @format int32
     * @min 0
     */
    start?: number;
    /**
     * The total number of documents.
     * @format int32
     */
    total?: number;
};

export { CustomerProductListType, CustomerProductListItemType };
export type { CustomerProductListItem, CustomerProductListResult, CustomerProductList };
